import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { RegisterAvatar, IdentityCheck, IdentityCheckSummary } from '@/features/photos/RegisterIdentity'
import { trainerApi, type AttendanceStatus, type AttendanceOutcome } from '@/api/trainer'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { CourseMaterialsSection } from './CourseMaterialsSection'
import {
  ChevronLeft, MapPin, Users, CheckCircle2, XCircle, Clock,
  BookOpen, ExternalLink, Ban, AlertTriangle, StickyNote, Globe,
} from 'lucide-react'
import { useTrainerDates } from '@/hooks/useDates'
import { LiveRoomCard } from '@/features/live/LiveRoomCard'
import { SignInPanel } from '@/features/attendance/SignInPanel'
import { useLiveStatus } from '@/features/live/useLiveStatus'
import { PageTabs, TabCount, TabLiveDot, TabPanel, usePageTab } from '@/components/shared/PageTabs'

function mapsUrl(address: string) {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
}

function initials(fullName: string) {
  const parts = fullName.trim().split(/\s+/)
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase()
}

const ATTENDANCE_CYCLE: AttendanceStatus[] = ['NotRecorded', 'Present', 'Absent']

function nextStatus(current: AttendanceStatus): AttendanceStatus {
  const idx = ATTENDANCE_CYCLE.indexOf(current)
  return ATTENDANCE_CYCLE[(idx + 1) % ATTENDANCE_CYCLE.length]
}

type AttendanceBadgeProps = { status: AttendanceStatus; disabled?: boolean; onClick?: () => void }

function AttendanceBadge({ status, disabled, onClick }: AttendanceBadgeProps) {
  const styles: Record<AttendanceStatus, string> = {
    Present:     'bg-green-100 text-green-700 border-green-300 hover:bg-green-200',
    Absent:      'bg-red-50 text-red-600 border-red-300 hover:bg-red-100',
    NotRecorded: 'bg-gray-100 text-gray-500 border-gray-200 hover:bg-gray-200',
  }
  const icons: Record<AttendanceStatus, React.ReactNode> = {
    Present:     <CheckCircle2 size={13} />,
    Absent:      <XCircle size={13} />,
    NotRecorded: <Clock size={13} />,
  }
  const labels: Record<AttendanceStatus, string> = {
    Present: 'Present', Absent: 'Absent', NotRecorded: 'Not recorded',
  }

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${styles[status]} ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      {icons[status]}
      {labels[status]}
    </button>
  )
}

type OutcomeToggleProps = { outcome: AttendanceOutcome; disabled?: boolean; onClick?: () => void }

function OutcomeToggle({ outcome, disabled, onClick }: OutcomeToggleProps) {
  const style = outcome === 'Failed'
    ? 'bg-amber-50 text-amber-700 border-amber-300 hover:bg-amber-100'
    : 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100'

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      title="Click to toggle pass/fail"
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${style} ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      {outcome === 'Failed' ? <Ban size={13} /> : <CheckCircle2 size={13} />}
      {outcome === 'Failed' ? 'Failed' : 'Passed'}
    </button>
  )
}

export default function TrainerSessionRegisterPage() {
  const { formatDate, today } = useTrainerDates()
  const fmtDate = (d: string) => {
    return formatDate(d + 'T00:00:00', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    })
  }
  const { sessionId } = useParams<{ sessionId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [completing, setCompleting] = useState(false)

  const { data: register, isLoading } = useQuery({
    queryKey: ['trainer-session-register', sessionId],
    queryFn: () => trainerApi.getSessionRegister(sessionId!).then(r => r.data),
    enabled: !!sessionId,
    staleTime: 0,
    refetchInterval: 30_000,
  })

  const attendanceMutation = useMutation({
    mutationFn: ({ attendeeId, status, outcome }: { attendeeId: string; status: AttendanceStatus; outcome?: AttendanceOutcome }) =>
      trainerApi.updateSessionAttendance(sessionId!, attendeeId, status, outcome),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['trainer-session-register', sessionId] }),
  })

  const completeMutation = useMutation({
    mutationFn: () => trainerApi.completeSession(sessionId!),
    onSuccess: () => {
      setCompleting(false)
      queryClient.invalidateQueries({ queryKey: ['trainer-session-register', sessionId] })
      queryClient.invalidateQueries({ queryKey: ['trainer-bookings'] })
      queryClient.invalidateQueries({ queryKey: ['session', sessionId] })
    },
  })

  const live = useLiveStatus({ sessionId: sessionId! }, !!sessionId)

  // Tabs: which one opens first depends on the day and whether a live session is running.
  const liveVisible = !!register && !!live.data?.enabled && !(register.isCancelled)
  const liveRunning = liveVisible && live.data?.delivery?.status === 'Open'
  const openSession = !!register && !(register.isCancelled || register.isCompleted)
  const defaultTab = !register || live.isLoading ? null
    : liveRunning ? 'live'
    : openSession && register.sessionDate <= today() ? 'attendance'
    : 'overview'
  const [tab, setTab] = usePageTab(['overview', 'attendance', ...(liveVisible ? ['live'] : []), 'materials'], defaultTab)

  if (isLoading || (!!register && live.isLoading)) return <p className="text-sm text-gray-400">Loading…</p>
  if (!register) return <p className="text-sm text-gray-500">Session not found.</p>

  const venueAddress = [register.venueAddressLine1, register.venueCity, register.venuePostcode]
    .filter(Boolean).join(', ')

  const presentCount = register.attendees.filter(a => a.attendanceStatus === 'Present').length
  const absentCount = register.attendees.filter(a => a.attendanceStatus === 'Absent').length
  const notRecordedCount = register.attendees.filter(a => a.attendanceStatus === 'NotRecorded').length
  const isLocked = register.isCompleted || register.isCancelled

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Back link */}
      <button type="button" onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 cursor-pointer">
        <ChevronLeft size={15} /> Back
      </button>

      {/* Cancelled banner */}
      {register.isCancelled && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-5 py-4 flex items-center gap-3">
          <Ban size={18} className="text-red-400 shrink-0" />
          <div>
            <p className="font-semibold text-red-800">Session cancelled</p>
            <p className="text-sm text-red-600">This session has been cancelled.</p>
          </div>
        </div>
      )}

      {register.isCompleted && (
        <div className="rounded-xl border border-green-200 bg-green-50 px-5 py-4 text-center">
          <CheckCircle2 size={20} className="mx-auto text-green-500 mb-2" />
          <p className="text-sm font-medium text-green-800">Session marked as complete</p>
          <p className="text-xs text-green-600 mt-0.5">An admin will review and issue certificates.</p>
        </div>
      )}

      {/* What this page is about, whichever tab is open */}
      <div>
        <h1 className="text-lg font-semibold text-gray-900">
          {register.courseName}
          {register.courseCode && <span className="ml-2 text-xs font-normal text-gray-400 bg-gray-100 rounded px-1.5 py-0.5">{register.courseCode}</span>}
        </h1>
        <p className="text-sm text-gray-500 mt-0.5">{fmtDate(register.sessionDate)}</p>
      </div>

      <PageTabs
        label="Session sections"
        active={tab}
        onChange={setTab}
        tabs={[
          { id: 'overview', label: 'Overview' },
          { id: 'attendance', label: 'Attendance', badge: register.attendees.length > 0 ? <TabCount>{presentCount} / {register.attendees.length}</TabCount> : undefined },
          ...(liveVisible ? [{ id: 'live', label: 'Live', badge: liveRunning ? <TabLiveDot label="A live session is running" /> : undefined }] : []),
          { id: 'materials', label: 'Materials' },
        ]}
      />

      <TabPanel id="overview" active={tab}>
        <Card>
          <CardContent className="space-y-4 text-sm pt-6">
            {/* Online delivery */}
            {register.deliveryMethod === 'Online' && (
              <div className="flex gap-3">
                <Globe size={15} className="shrink-0 text-indigo-400 mt-0.5" />
                <div>
                  <p className="font-medium text-gray-800">Online</p>
                  {register.meetingUrl && (
                    <a href={register.meetingUrl} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline break-all">
                      {register.meetingUrl}
                    </a>
                  )}
                  {register.meetingInstructions && (
                    <p className="text-gray-500 mt-1 whitespace-pre-line">{register.meetingInstructions}</p>
                  )}
                </div>
              </div>
            )}

            {/* Venue */}
            {register.deliveryMethod === 'InPerson' && register.venueName && (
              <div className="space-y-2">
                <div className="flex gap-3">
                  <MapPin size={15} className="shrink-0 text-indigo-400 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-800">{register.venueName}</p>
                    {venueAddress && (
                      <a
                        href={mapsUrl(venueAddress)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-500 hover:text-indigo-600 mt-0.5 inline-flex items-start gap-1 group"
                      >
                        <span>{venueAddress}</span>
                        <ExternalLink size={11} className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity mt-0.5" />
                      </a>
                    )}
                    {register.venueNotes && (
                      <p className="text-gray-400 mt-1 text-xs italic">{register.venueNotes}</p>
                    )}
                  </div>
                </div>
                {venueAddress && (
                  <div className="rounded-lg overflow-hidden border border-gray-200 h-48">
                    <iframe
                      title="Venue location"
                      src={`https://maps.google.com/maps?q=${encodeURIComponent(venueAddress)}&output=embed`}
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Trainer team */}
            <div className="space-y-1">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">Your role</p>
              <p className="text-sm text-gray-700 font-medium">{register.myRole}</p>
              {register.coTrainers.length > 0 && (
                <div className="mt-1 space-y-0.5">
                  {register.coTrainers.map((t, i) => (
                    <p key={i} className="text-xs text-gray-500">
                      {t.firstName} {t.lastName}
                      <span className="ml-1 text-gray-400">· {t.role}</span>
                    </p>
                  ))}
                </div>
              )}
            </div>

            {/* Session notes */}
            {register.notes && (
              <p className="text-gray-500 italic">{register.notes}</p>
            )}

            <p className="text-gray-400">{register.bookedPlaces} / {register.totalPlaces} places booked · {register.attendees.length} attendee{register.attendees.length !== 1 ? 's' : ''}</p>
          </CardContent>
        </Card>

      </TabPanel>

      <TabPanel id="attendance" active={tab}>
        <div className="mb-4">
          <SignInPanel
            target={{ sessionId: register.sessionId }}
            hidden={register.isCompleted || register.isCancelled}
            onChanged={() => queryClient.invalidateQueries({ queryKey: ['trainer-session-register', sessionId] })}
          />
        </div>
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Users size={15} className="text-indigo-500" />
                Attendance register
              </CardTitle>
            </div>
            {/* Summary bar */}
            {register.attendees.length > 0 && (
              <div className="flex gap-4 text-xs mt-2">
                <span className="text-green-600 font-medium">{presentCount} present</span>
                <span className="text-red-500 font-medium">{absentCount} absent</span>
                <span className="text-gray-400">{notRecordedCount} not recorded</span>
              </div>
            )}
          </CardHeader>
          <CardContent>
            <IdentityCheckSummary
              required={register.photosEnabled && register.requirePhotoCheck}
              locked={isLocked}
              outstanding={register.attendees.filter(a => !a.identityCheckedAt).length}
            />
            {register.attendees.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4">No confirmed attendees yet.</p>
            ) : (
              <div className="space-y-1.5">
                {register.attendees.map(a => (
                  <div key={a.attendeeId} className="flex items-center gap-3 rounded-lg border border-gray-100 px-3 py-2.5">
                    {/* Avatar: the attendee's photo when photos are on */}
                    <RegisterAvatar
                      source={{
                        key: `trainer-session:${a.attendeeId}`,
                        name: a.fullName,
                        version: a.photoUpdatedAt,
                        enabled: register.photosEnabled,
                        canEdit: !isLocked,
                        notice: register.photoNotice,
                        load: () => trainerApi.getSessionAttendeePhoto(sessionId!, a.attendeeId).then(r => r.data),
                        upload: photo => trainerApi.setSessionAttendeePhoto(sessionId!, a.attendeeId, photo),
                        onChanged: () => queryClient.invalidateQueries({ queryKey: ['trainer-session-register', sessionId] }),
                      }}
                      fallback={
                        <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 text-xs font-semibold shrink-0">
                          {initials(a.fullName)}
                        </div>
                      }
                    />
                    {/* Name + email */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{a.fullName}</p>
                      <p className="text-xs text-gray-400 truncate">{a.email}{a.companyName ? ` · ${a.companyName}` : ''}</p>
                      {a.bookingAdminNotes && (
                        <p className="text-xs text-indigo-600 mt-0.5 flex items-start gap-1">
                          <StickyNote size={11} className="shrink-0 mt-0.5" />
                          <span>{a.bookingAdminNotes}</span>
                        </p>
                      )}
                      <IdentityCheck
                        enabled={register.photosEnabled}
                        required={register.requirePhotoCheck}
                        locked={isLocked}
                        hasPhoto={!!a.photoUpdatedAt}
                        checkedAt={a.identityCheckedAt}
                        checkedBy={a.identityCheckedBy}
                        onToggle={checked => trainerApi.setSessionIdentityCheck(sessionId!, a.attendeeId, checked)
                          .then(() => queryClient.invalidateQueries({ queryKey: ['trainer-session-register', sessionId] }))}
                      />
                    </div>
                    {/* Pass/fail toggle — only meaningful once Present */}
                    {a.attendanceStatus === 'Present' && (
                      <OutcomeToggle
                        outcome={a.outcome ?? 'Passed'}
                        disabled={isLocked || attendanceMutation.isPending}
                        onClick={() => attendanceMutation.mutate({
                          attendeeId: a.attendeeId,
                          status: 'Present',
                          outcome: a.outcome === 'Failed' ? 'Passed' : 'Failed',
                        })}
                      />
                    )}
                    {/* Attendance toggle */}
                    <AttendanceBadge
                      status={a.attendanceStatus}
                      disabled={isLocked || attendanceMutation.isPending}
                      onClick={() => attendanceMutation.mutate({ attendeeId: a.attendeeId, status: nextStatus(a.attendanceStatus) })}
                    />
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {!isLocked && (
          <div className="border-t pt-4 space-y-3">
            {completing ? (
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-5 py-4 space-y-3">
                <p className="font-medium text-amber-800">
                  Mark session as complete?
                </p>
                <p className="text-sm text-amber-700">
                  {presentCount} attendee{presentCount !== 1 ? 's' : ''} marked present. An admin will issue certificates afterwards.
                </p>
                {notRecordedCount > 0 && (
                  <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-100 px-3 py-2.5">
                    <AlertTriangle size={14} className="text-amber-600 mt-0.5 shrink-0" />
                    <p className="text-xs text-amber-800">
                      {notRecordedCount} attendee{notRecordedCount !== 1 ? 's' : ''} {notRecordedCount !== 1 ? "haven't" : "hasn't"} been marked yet — their attendance won't be recorded.
                    </p>
                  </div>
                )}
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => completeMutation.mutate()}
                    disabled={completeMutation.isPending}
                  >
                    {completeMutation.isPending ? 'Completing…' : 'Yes, complete session'}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setCompleting(false)}>Cancel</Button>
                </div>
              </div>
            ) : (
              <Button
                onClick={() => setCompleting(true)}
                disabled={presentCount === 0}
                className="w-full"
              >
                {presentCount === 0 ? 'Mark at least one attendee present to complete' : `Mark session complete — ${presentCount} present`}
              </Button>
            )}
          </div>
        )}
      </TabPanel>

      {liveVisible && (
        <TabPanel id="live" active={tab}>
          <LiveRoomCard audience="trainer" target={{ sessionId: register.sessionId }} hidden={register.isCancelled} />
        </TabPanel>
      )}

      <TabPanel id="materials" active={tab}>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <BookOpen size={15} className="text-indigo-500" />
              Course materials
            </CardTitle>
          </CardHeader>
          <CardContent>
            <CourseMaterialsSection courseId={register.courseId} />
          </CardContent>
        </Card>

      </TabPanel>

    </div>
  )
}
